import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Package, Calendar, ExternalLink, AlertCircle, Camera, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CAMERA_CATEGORIES, GE_CATEGORIES } from "@/types/gear";

export default function PublicInventoryView() {
  const { token } = useParams();

  const { data: shareData, isLoading, error } = useQuery({
    queryKey: ["public-inventory-share", token],
    queryFn: async () => {
      if (!token) throw new Error("No token provided");

      console.log("Looking for inventory share with token:", token);

      // CRITICAL: Only SELECT data, never INSERT/UPDATE/DELETE anything
      // First, validate the token and get user info - READ ONLY
      const { data: userShare, error: userShareError } = await supabase
        .from("user_inventory_shares")
        .select(`
          user_id,
          is_public_shared,
          public_share_revoked,
          public_share_expiration,
          public_share_token
        `)
        .eq("public_share_token", token)
        .eq("is_public_shared", true)
        .eq("public_share_revoked", false)
        .maybeSingle();

      console.log("User share query result:", { userShare, userShareError });

      if (userShareError) {
        console.error("Database error in user_inventory_shares:", userShareError);
        throw new Error(`Database error: ${userShareError.message}`);
      }

      if (!userShare) {
        console.log("No valid user share found for token:", token);
        throw new Error("Share link not found or has been revoked");
      }

      // Check if share has expired
      if (userShare.public_share_expiration && new Date(userShare.public_share_expiration) < new Date()) {
        console.log("Share has expired:", userShare.public_share_expiration);
        throw new Error("Share link has expired");
      }

      // Get user profile info - READ ONLY, no modifications
      const { data: userProfile, error: profileError } = await supabase
        .from("profiles")
        .select("full_name, email")
        .eq("id", userShare.user_id)
        .maybeSingle();

      console.log("User profile query result:", { userProfile, profileError });

      // CRITICAL: Only SELECT inventory items, never INSERT/UPDATE/DELETE
      // Fetch items belonging ONLY to the shared user - completely read-only
      const { data: items, error: itemsError } = await supabase
        .from("inventory_items")
        .select("*")
        .eq("user_id", userShare.user_id)
        .order("category", { ascending: true })
        .order("name", { ascending: true });

      console.log("Items query result:", { items, itemsError });

      if (itemsError) {
        console.error("Error fetching items:", itemsError);
        throw new Error(`Failed to fetch inventory items: ${itemsError.message}`);
      }

      // IMPORTANT: Completely avoid any write operations to prevent affecting other users
      // Don't update access counts or any other data that could interfere with user inventories

      // Return ONLY read-only data - no modifications to any database records
      return {
        user: userProfile,
        items: items || []
      };
    },
    enabled: !!token,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto space-y-6">
            <Skeleton className="h-20 w-full" />
            <div className="grid gap-6 md:grid-cols-2">
              <Skeleton className="h-64" />
              <Skeleton className="h-64" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    console.error("Page error:", error);
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md mx-auto px-4">
          <AlertCircle className="h-16 w-16 text-red-400 mx-auto" />
          <h1 className="text-2xl font-bold text-white">Link Not Available</h1>
          <p className="text-gray-300">{error.message}</p>
          <Button 
            onClick={() => window.location.href = 'https://www.preptcine.com'}
            className="mt-4"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Visit PREPT
          </Button>
        </div>
      </div>
    );
  }

  if (!shareData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md mx-auto px-4">
          <AlertCircle className="h-16 w-16 text-red-400 mx-auto" />
          <h1 className="text-2xl font-bold text-white">No Data Available</h1>
          <p className="text-gray-300">Unable to load inventory data</p>
          <Button 
            onClick={() => window.location.href = 'https://www.preptcine.com'}
            className="mt-4"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Visit PREPT
          </Button>
        </div>
      </div>
    );
  }

  const { user, items } = shareData;

  // Group items by specific categories - READ ONLY operations
  const cameraItems = items.filter(item => CAMERA_CATEGORIES.includes(item.category as any));
  const geItems = items.filter(item => GE_CATEGORIES.includes(item.category as any));

  // Function to group items by category within a department - READ ONLY
  const groupByCategory = (items: any[]) => {
    return items.reduce((acc, item) => {
      if (!acc[item.category]) {
        acc[item.category] = [];
      }
      acc[item.category].push(item);
      return acc;
    }, {} as Record<string, any[]>);
  };

  const cameraItemsByCategory = groupByCategory(cameraItems);
  const geItemsByCategory = groupByCategory(geItems);

  // Calculate totals - READ ONLY operations
  const totalItems = items.length;
  const totalValue = items.reduce((sum, item) => sum + ((item.cost_per_unit || 0) * (item.quantity || 1)), 0);

  const renderCategorySection = (categoryName: string, categoryItems: any[], icon: any) => {
    if (!Array.isArray(categoryItems) || categoryItems.length === 0) return null;

    return (
      <div key={categoryName} className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          {icon}
          <h4 className="text-lg font-semibold text-white">{categoryName}</h4>
          <Badge variant="outline" className="text-gray-300 border-gray-600 text-xs">
            {categoryItems.length} items
          </Badge>
        </div>
        <div className="bg-gray-800/30 border border-gray-700 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-700">
                <TableHead className="text-gray-300 font-medium text-left">Item</TableHead>
                <TableHead className="text-gray-300 font-medium text-center w-20">Qty</TableHead>
                <TableHead className="text-gray-300 font-medium text-right w-32">Cost/Unit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categoryItems.map((item) => (
                <TableRow key={item.id} className="border-gray-700 hover:bg-gray-700/30">
                  <TableCell className="text-white font-medium">{item.name}</TableCell>
                  <TableCell className="text-center text-gray-300">{item.quantity || 1}</TableCell>
                  <TableCell className="text-right text-gray-300">
                    ${(item.cost_per_unit || 0).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Package className="h-8 w-8 text-blue-400" />
              <span className="text-sm font-medium text-gray-400 bg-gray-800 px-3 py-1 rounded-full">
                SHARED INVENTORY - READ ONLY
              </span>
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">
              {user?.full_name ? `${user.full_name}'s Inventory` : 'Shared Inventory'}
            </h1>
            <div className="flex items-center justify-center gap-4 text-sm text-gray-400">
              <Badge variant="outline" className="text-gray-300 border-gray-600">
                {totalItems} items
              </Badge>
              {totalValue > 0 && (
                <Badge variant="outline" className="text-gray-300 border-gray-600">
                  ${totalValue.toLocaleString()} total value
                </Badge>
              )}
            </div>
          </div>

          {/* Inventory Content - READ ONLY DISPLAY */}
          <div className="space-y-8">
            {/* Camera Department */}
            {Object.keys(cameraItemsByCategory).length > 0 && (
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Camera className="h-5 w-5 text-blue-400" />
                    Camera Department ({cameraItems.length} items)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.entries(cameraItemsByCategory).map(([category, categoryItems]) =>
                    renderCategorySection(category, categoryItems as any[], <div className="w-2 h-2 bg-blue-400 rounded-full"></div>)
                  )}
                </CardContent>
              </Card>
            )}

            {/* G&E Department */}
            {Object.keys(geItemsByCategory).length > 0 && (
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Lightbulb className="h-5 w-5 text-yellow-400" />
                    G&E Department ({geItems.length} items)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.entries(geItemsByCategory).map(([category, categoryItems]) =>
                    renderCategorySection(category, categoryItems as any[], <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>)
                  )}
                </CardContent>
              </Card>
            )}

            {items.length === 0 && (
              <Card className="bg-gray-800/50 border-gray-700">
                <CardContent className="py-12 text-center">
                  <Package className="h-16 w-16 text-gray-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-300 mb-2">No Items Yet</h3>
                  <p className="text-gray-500">This inventory doesn't have any items yet.</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Footer */}
          <div className="mt-12 text-center py-8 border-t border-gray-700">
            <p className="text-sm text-gray-500 mb-4">
              Powered by PREPT - Professional Film Equipment Management
            </p>
            <Button 
              onClick={() => window.open('https://www.preptcine.com', '_blank')}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Get PREPT for Your Projects
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
